'use server'

import { apiRequest, ApiResponse } from '../request'

const BASE = 'master/whatsapp-accounts'

/** Situação da conta de uma organização. O token nunca vem nesta resposta. */
export interface WhatsappAccountStatusResult {
    configured: boolean
    wabaId?: string
    status?: 'PENDING' | 'ACTIVE' | 'ERROR'
    tokenExpiresAt?: string | null
    lastError?: string | null
    updatedAt?: string
}

/** Um número da conta, como a Meta o devolve. */
export interface WhatsappAccountPhoneNumber {
    /** É este valor que vai em `Department.whatsappPhoneNumberId`. */
    id: string
    displayPhoneNumber: string
    verifiedName?: string
    qualityRating?: string
    /** `CONNECTED` = registrado e apto a enviar; `PENDING` = falta registrar. */
    status?: string
    codeVerificationStatus?: string
}

export interface WhatsappAccountVerifyResult {
    success: boolean
    wabaId: string
    phoneNumbers: WhatsappAccountPhoneNumber[]
    /** Apps inscritos na conta. Sem nenhum, a conta LÊ mas não ENVIA. */
    subscribedApps?: { id: string; name?: string }[]
    /** `true` só quando o NOSSO app está entre os inscritos. */
    appSubscribed?: boolean
    ourAppId?: string
    /** Escopos reais do token, direto da Meta. */
    tokenScopes?: string[]
    tokenGranularScopes?: { scope: string; targetIds?: string[] }[]
    /** `false` = o token não pode enviar, por mais que leia a conta. */
    canSendMessages?: boolean | null
    /** A qual app o token pertence — precisa ser o nosso. */
    tokenAppId?: string
    tokenType?: string
    tokenValid?: boolean
    tokenAppMatches?: boolean
}

/** Uma conta já cadastrada, com o nome da organização para exibição. */
export interface ConfiguredWhatsappAccount {
    organizationId: number
    organizationName: string
    wabaId: string
    status: 'PENDING' | 'ACTIVE' | 'ERROR'
    tokenExpiresAt: string | null
    lastError: string | null
}

export async function listWhatsappAccounts(): Promise<
    ApiResponse<ConfiguredWhatsappAccount[]>
> {
    return apiRequest<ConfiguredWhatsappAccount[]>(`${BASE}`, {}, 'get')
}

export async function getWhatsappAccount(
    organizationId: number,
): Promise<ApiResponse<WhatsappAccountStatusResult>> {
    return apiRequest<WhatsappAccountStatusResult>(`${BASE}/${organizationId}`, {}, 'get')
}

export async function saveWhatsappAccount(payload: {
    organizationId: number
    wabaId: string
    accessToken: string
    tokenExpiresAt?: string
}): Promise<ApiResponse<{ success: boolean; wabaId: string; status: string }>> {
    return apiRequest(`${BASE}`, { data: payload }, 'post')
}

/** Testa as credenciais contra a Meta e devolve os números (com o `phoneNumberId`). */
export async function verifyWhatsappAccount(
    organizationId: number,
): Promise<ApiResponse<WhatsappAccountVerifyResult>> {
    return apiRequest<WhatsappAccountVerifyResult>(`${BASE}/${organizationId}/verify`, {}, 'post')
}

export async function disconnectWhatsappAccount(
    organizationId: number,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(`${BASE}/${organizationId}`, {}, 'delete')
}

/** Setor da organização e o número da Meta vinculado a ele. */
export interface WhatsappDepartmentLink {
    id: number
    name: string
    whatsappNumber: string | null
    whatsappProvider: string
    whatsappPhoneNumberId: string | null
}

export async function getWhatsappDepartments(
    organizationId: number,
): Promise<ApiResponse<WhatsappDepartmentLink[]>> {
    return apiRequest<WhatsappDepartmentLink[]>(`${BASE}/${organizationId}/departments`, {}, 'get')
}

/** `phoneNumberId` nulo desfaz o vínculo e devolve o setor ao provedor legado. */
export async function linkWhatsappPhoneNumber(
    organizationId: number,
    departmentId: number,
    phoneNumberId: string | null,
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest(
        `${BASE}/${organizationId}/departments/${departmentId}/phone-number`,
        { data: { phoneNumberId } },
        'post',
    )
}

/**
 * Envia uma mensagem REAL pela API oficial. Usa template porque texto livre só
 * é entregue dentro da janela de 24h.
 */
export async function sendWhatsappTestMessage(
    organizationId: number,
    payload: {
        phoneNumberId: string
        to: string
        templateName: string
        language?: string
        params?: string[]
    },
): Promise<ApiResponse<{ success: boolean; messageId: string; waId?: string }>> {
    return apiRequest(`${BASE}/${organizationId}/test-message`, { data: payload }, 'post')
}

/**
 * Inscreve o app na conta. É o que habilita enviar e receber por ela — ler
 * funciona só com o token, e por isso a falta aparece apenas no envio.
 */
export async function subscribeWhatsappApp(
    organizationId: number,
): Promise<ApiResponse<{ success: boolean; subscribedApps: { id: string; name?: string }[] }>> {
    return apiRequest(`${BASE}/${organizationId}/subscribe-app`, {}, 'post')
}

/**
 * Registra o número na Cloud API — é o que habilita o envio por ele.
 * O PIN é a verificação em duas etapas do número e passa a ser exigido para
 * registrá-lo em qualquer app daí em diante.
 */
export async function registerWhatsappPhoneNumber(
    organizationId: number,
    phoneNumberId: string,
    pin: string,
): Promise<ApiResponse<{ success: boolean; phoneNumberId: string }>> {
    return apiRequest(
        `${BASE}/${organizationId}/phone-numbers/${phoneNumberId}/register`,
        { data: { pin } },
        'post',
    )
}

/**
 * Inscreve o app numa WABA específica. A conta de TESTE da Meta é uma WABA à
 * parte: sem inscrever nela, o envio funciona mas nenhum status volta.
 */
export async function subscribeWhatsappAppToWaba(
    organizationId: number,
    wabaId: string,
): Promise<ApiResponse<{ success: boolean; wabaId: string; appSubscribed: boolean }>> {
    return apiRequest(`${BASE}/${organizationId}/subscribe-app/${wabaId}`, {}, 'post')
}
